import React, { useCallback, useEffect, useMemo, useState } from "react";
import MForm from "~/app/modules/form";
import Select from "~/app/modules/form/Select";
import Date from "~/app/modules/form/Date";
import useForm from "~/app/modules/form/useForm";
import useTranslation from "~/app/hooks/useTranslation";
import { useStoreState } from "~/store";
import { CreateWorkOrderFormValues } from "~/workorder/types";
import classes from "./style.module.scss";
import Loader from "~/app/common/Loader";
import moment from "moment";
import { formatDate } from "~/app/helpers/date";
import { computePlannedStartEnd, hasValidWorkOrderHoursCount } from "./helper";

export type SecondaryFormComponent = React.FC;

const SecondaryForm: SecondaryFormComponent = () => {
  const { t } = useTranslation("workorder.create");
  const [ initPlannedDates, setInitPlannedDates ] = useState(false);
  const { values, fieldEvents, setValue } = useForm<CreateWorkOrderFormValues>({
    formName: "createWorkOrder",
  });
  const users = useStoreState((state) => {
    const departmentId = Number(values?.departmentId ?? 0);
    return state.workOrder.departments.find(department => department.id === departmentId)?.users ?? [];
  });
  const workOrderGeneralSettings = useStoreState((state) => state.workOrder.workOrderGeneralSettings);

  const usersOptions = useMemo(() => users.map((user) => ({
    text: user.name,
    value: user.id,
  })), [users]);

  const departmentId = Number(values?.departmentId ?? 0);
  const hasDepartment = departmentId > 0;

  useEffect(() => {
    if (!values || initPlannedDates) {
      return;
    }
    const startEmpty = !String(values.plannedStartDate ?? "").trim();
    const endEmpty = !String(values.plannedEndDate ?? "").trim();
    if (!startEmpty || !endEmpty) {
      setInitPlannedDates(true);
      return;
    }
    const { plannedStartDate, plannedEndDate } = computePlannedStartEnd(
      moment(),
      workOrderGeneralSettings
    );
    setValue("plannedStartDate", plannedStartDate);
    setValue("plannedEndDate", plannedEndDate);
    setInitPlannedDates(true);
  }, [
    values,
    values?.plannedStartDate,
    values?.plannedEndDate,
    workOrderGeneralSettings,
    setValue,
    initPlannedDates,
  ]);

  const handlePlannedStartChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      fieldEvents.onChange(e);
      const newStart = e.target.value;
      if (
        hasValidWorkOrderHoursCount(workOrderGeneralSettings.hoursCount) &&
        newStart &&
        moment(newStart).isValid()
      ) {
        const end = moment(newStart).add(
          Number(workOrderGeneralSettings.hoursCount) * 60,
          "minutes"
        );
        setValue("plannedEndDate", formatDate(end, "YYYY-MM-DD[T]HH:mm"));
      }
    },
    [fieldEvents, workOrderGeneralSettings.hoursCount, setValue]
  );

  const minPlannedStartDate = useMemo(() => {
    return moment().format("YYYY-MM-DD[T]HH:mm");
  }, []);

  const minPlannedEndDate = useMemo(() => {
    const start = values?.plannedStartDate;
    return start ? String(start) : undefined;
  }, [values?.plannedStartDate]);

  if (!initPlannedDates || !usersOptions.length) {
    return <Loader size="32" />;
  }

  return (
    <div className={classes.card}>
      <MForm className={classes.form}>
        <Select
          key={String(values.departmentId ?? 0)}
          className={classes["input-container"]}
          label={
            <div>
              {t("fields.responsible")}{" "}
              <span className={classes["required-mark"]}>*</span>
            </div>
          }
          name="responsibleId"
          placeholder={t("placeholders.responsible")}
          options={hasDepartment ? usersOptions : []}
          icon="keyboard_arrow_down"
          onChange={fieldEvents.onChange}
          defaultValue={String(values.responsibleId ?? 0)}
          showEmptyOption
          emptyOptionValue={0}
        />
        <Date
          className={classes["input-container"]}
          label={
            <div>
              {t("fields.plannedStartDate")}{" "}
              <span className={classes["required-mark"]}>*</span>
            </div>
          }
          strongTime
          name="plannedStartDate"
          format="DD.MM.YYYY HH:mm"
          min={minPlannedStartDate}
          defaultValue={String(values.plannedStartDate ?? "")}
          onChange={handlePlannedStartChange}
        />
        <Date
          className={classes["input-container"]}
          label={
            <div>
              {t("fields.plannedEndDate")}{" "}
              <span className={classes["required-mark"]}>*</span>
            </div>
          }
          strongTime
          name="plannedEndDate"
          format="DD.MM.YYYY HH:mm"
          defaultValue={String(values.plannedEndDate ?? "")}
          onChange={fieldEvents.onChange}
          min={minPlannedEndDate}
        />
      </MForm>
    </div>
  );
};

export default SecondaryForm;
